import type { BuildContext, Obstacle } from '../types';
import type { Vec2 } from '../../geo/types';
import type { PlacedShip } from './shipPlacement';
import { cargoGround } from '../life/cargoGround';
import { planBounds, planFrameOf, toLayout } from '../fabric/landmarks/planFrame';
import { lighterPath, lighterWater } from './tradeWater';
import { tradeCartRoute } from './tradeCart';

export interface HarbourTrade {
  readonly ship: PlacedShip;
  readonly water: readonly Vec2[];
  readonly cart: readonly Vec2[];
  readonly quay: Vec2;
  readonly scale: Vec2;
  readonly heading: number;
  readonly store: Vec2;
  readonly arrival: number;
}

function shipWaterRoute(
  ships: readonly PlacedShip[],
  berth: Vec2,
  water: ReturnType<typeof lighterWater>
) {
  for (const ship of ships)
    for (const side of [1, -1]) {
      const distance = ship.footprint.halfV + 6;
      const from: Vec2 = [
        ship.position[0] - Math.sin(ship.heading) * distance * side,
        ship.position[1] + Math.cos(ship.heading) * distance * side,
      ];
      const path = lighterPath(from, berth, water);
      if (path) return { ship, path };
    }
  return null;
}

export function planHarbourTrade(
  ctx: BuildContext,
  ships: readonly PlacedShip[],
  barriers: readonly Obstacle[]
): HarbourTrade | null {
  const waag = ctx.layout.landmarks.find((l) => l.id === 'landmark-waag-dam');
  const quay = ctx.layout.streets.find((s) => s.id === 'quay-damrak-west');
  if (!waag || !quay) return null;
  const plan = planFrameOf(waag.footprint, waag.heading),
    bounds = planBounds(plan);
  const ground = cargoGround(ctx.layout, ctx.site, ctx.lots);
  const water = lighterWater(ctx.site, barriers);
  const a = quay.centerline[0],
    b = quay.centerline.at(-1)!;
  const length = Math.hypot(a[0] - b[0], a[1] - b[1]);
  const dx = (a[0] - b[0]) / length,
    dy = (a[1] - b[1]) / length;
  const candidates = ships
    .filter((s) => s.type === 'hulk' || s.type === 'kraeck' || s.type === 'bark')
    .sort(
      (s, t) =>
        Math.hypot(s.position[0] - a[0], s.position[1] - a[1]) -
        Math.hypot(t.position[0] - a[0], t.position[1] - a[1])
    );
  for (const offset of [25, 35, 45, 65, 85, 105, 125]) {
    const loading: Vec2 = [b[0] + dx * offset, b[1] + dy * offset];
    const berth = [7, 9, 11]
      .map((distance): Vec2 => [loading[0] + dy * distance, loading[1] - dx * distance])
      .find(water.open);
    if (!berth) continue;
    for (const u of [0, -3, 3]) {
      const scale = toLayout(plan, [u, bounds.v1 + 3]);
      const cartEnd = toLayout(plan, [u, bounds.v1 + 6.5]);
      const store = toLayout(plan, [u + 3.4, bounds.v1 + 1.2]);
      const delivery = tradeCartRoute(loading, cartEnd, ground);
      if (
        !delivery ||
        !ground.open(store) ||
        ![
          [-2.3, -1.3],
          [-2.3, 1.3],
          [2.3, -1.3],
          [2.3, 1.3],
        ].every(([x, y]) => ground.open(toLayout(plan, [u + x!, bounds.v1 + 3 + y!])))
      )
        continue;
      const voyage = shipWaterRoute(candidates.slice(0, 10), berth, water);
      if (voyage)
        return {
          ship: voyage.ship,
          water: voyage.path,
          ...delivery,
          quay: loading,
          scale,
          heading: plan.heading,
          store,
        };
    }
  }
  return null;
}
